import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Button, List, ListItem, ListItemText, Chip, CircularProgress, Alert, Divider
} from '@mui/material';
import useDataFetch, { usePostDataFetch } from '../../utils/useDataFetch';
import { useAuth } from '../../context/AuthContext';

const AdminApprovals = () => {
    const { user } = useAuth();

    // Fetch pending teams and users
    const { data: teamsData, error: teamsError, isLoading: teamsLoading, refetch: refetchTeams } = useDataFetch({
        url: '/admin/pending-teams'
    });
    const { data: usersData, error: usersError, isLoading: usersLoading, refetch: refetchUsers } = useDataFetch({
        url: '/admin/pending-users'
    });

    const { postData: approveTeam } = usePostDataFetch();
    const { postData: rejectTeam } = usePostDataFetch();
    const { postData: approveUser } = usePostDataFetch();
    const { postData: rejectUser } = usePostDataFetch();

    const isAdmin = user?.isAdmin || user?.role === 'super_admin';
    if (!isAdmin) {
        return <Alert severity="error">Zugriff verweigert. Nur Administratoren können diese Seite aufrufen.</Alert>;
    }

    const handleApproveTeam = async (id) => {
        await approveTeam({ url: `/admin/approve-team/${id}`, aBody: {} });
        refetchTeams();
    };

    const handleRejectTeam = async (id) => {
        await rejectTeam({ url: `/admin/reject-team/${id}`, aBody: {} });
        refetchTeams();
    };

    const handleApproveUser = async (id) => {
        await approveUser({ url: `/admin/approve-user/${id}`, aBody: {} });
        refetchUsers();
    };

    const handleRejectUser = async (id) => {
        await rejectUser({ url: `/admin/reject-user/${id}`, aBody: {} });
        refetchUsers();
    };

    if (teamsLoading || usersLoading) return <CircularProgress />;
    if (teamsError || usersError) return <Alert severity="error">Fehler beim Laden der Daten</Alert>;

    const pendingTeams = teamsData?.teams || [];
    const pendingUsers = usersData?.users || [];

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 1000, mx: 'auto' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Admin - Genehmigungen</Typography>

            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>Pending Teams</Typography>
                    {pendingTeams.length === 0 ? (
                        <Typography>Keine pending Teams</Typography>
                    ) : (
                        <List>
                            {pendingTeams.map(team => (
                                <React.Fragment key={team.id}>
                                    <ListItem>
                                        <ListItemText
                                            primary={team.name}
                                            secondary={`Erstellt von: ${team.creator_email}`}
                                        />
                                        <Button variant="contained" color="success" onClick={() => handleApproveTeam(team.id)} sx={{ mr: 1 }}>
                                            Genehmigen
                                        </Button>
                                        <Button variant="contained" color="error" onClick={() => handleRejectTeam(team.id)}>
                                            Ablehnen
                                        </Button>
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography variant="h5" gutterBottom>Pending Users</Typography>
                    {pendingUsers.length === 0 ? (
                        <Typography>Keine pending Users</Typography>
                    ) : (
                        <List>
                            {pendingUsers.map(u => (
                                <React.Fragment key={u.id}>
                                    <ListItem>
                                        <ListItemText
                                            primary={`${u.first_name} ${u.last_name}`}
                                            secondary={`E-Mail: ${u.email} | Rolle: ${u.role}`}
                                        />
                                        <Button variant="contained" color="success" onClick={() => handleApproveUser(u.id)} sx={{ mr: 1 }}>
                                            Genehmigen
                                        </Button>
                                        <Button variant="contained" color="error" onClick={() => handleRejectUser(u.id)}>
                                            Ablehnen
                                        </Button>
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default AdminApprovals;